import React, {useState, useRef, useEffect, useCallback} from 'react'
import {
  TabGroupWrapper,
  TabList,
  Tab,
  TabLabel,
  TabInput,
  CharCount,
  ActionButtonWrapper,
} from './TabGroup.styles'
import {Button} from '../../../../../../core/shared/Button'
import Icon from '../../../../../../core/shared/Icon'

// ─── Auto-size hook for the inline input ─────────────────────────────────────

function useInputAutoWidth(value, ref) {
  useEffect(() => {
    if (!ref.current) return
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const style = window.getComputedStyle(ref.current)
    ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
    const width = ctx.measureText(value || ' ').width
    ref.current.style.width = `${Math.ceil(width) + 4}px`
  }, [value, ref])
}

// ─── Single editable tab ──────────────────────────────────────────────────────

function EditableTab({
  tab,
  active,
  onSelect,
  onRename,
  onCopy,
  onDelete,
  disableCopy,
  disableDelete,
}) {
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState(tab.label)
  const inputRef = useRef(null)

  useInputAutoWidth(draftName, inputRef)

  // Focus input when editing starts
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const commitEdit = useCallback(() => {
    const trimmed = draftName.trim()
    if (trimmed) onRename(tab.id, trimmed)
    else setDraftName(tab.label) // revert if empty
    setEditing(false)
  }, [draftName, tab.id, tab.label, onRename])

  const handleKeyDown = e => {
    if (e.key === 'Enter') commitEdit()
    if (e.key === 'Escape') {
      setDraftName(tab.label)
      setEditing(false)
    }
  }

  const handleEditClick = e => {
    e.stopPropagation()
    setDraftName(tab.label)
    setEditing(true)
  }

  return (
    <Tab
      active={active}
      onClick={() => !editing && onSelect(tab.id)}
      role="tab"
      aria-selected={active}
      tabIndex={active ? 0 : -1}
    >
      {editing ? (
        <>
          <TabInput
            ref={inputRef}
            value={draftName}
            onChange={e => setDraftName(e.target.value.slice(0, 50))}
            onBlur={commitEdit}
            onKeyDown={handleKeyDown}
            onClick={e => e.stopPropagation()}
            aria-label="Rename tab"
            maxLength={50}
          />
          <CharCount warn={draftName.length >= 45}>
            {draftName.length}/50
          </CharCount>
        </>
      ) : (
        <TabLabel active={active} title={tab.label}>
          {tab.label}
        </TabLabel>
      )}

      <ActionButtonWrapper>
        <Button
          variant="action-icon"
          size="xsm"
          onClick={handleEditClick}
          aria-label={`Rename ${tab.label}`}
          // title="Rename"
        >
          <Icon name="edit-text" size="xs" />
        </Button>

        <Button
          variant="action-icon"
          size="xsm"
          disabled={disableCopy}
          aria-disabled={disableCopy}
          onClick={e => {
            e.stopPropagation()
            if (disableCopy) return
            onCopy(tab.id)
          }}
          aria-label={`Duplicate ${tab.label}`}
          title={
            disableCopy ? 'Maximum number of proposals reached' : 'Duplicate'
          }
        >
          <Icon name="copy" size="xs" />
        </Button>

        <Button
          variant="action-icon"
          size="xsm"
          disabled={disableDelete}
          aria-disabled={disableDelete}
          onClick={e => {
            e.stopPropagation()
            if (disableDelete) return
            onDelete(tab.id)
          }}
          aria-label={`Delete ${tab.label}`}
          title={disableDelete ? 'At least one proposal is required' : 'Delete'}
        >
          <Icon name="close" size="xs" />
        </Button>
      </ActionButtonWrapper>
    </Tab>
  )
}

// ─── TabGroup ─────────────────────────────────────────────────────────────────

export function TabGroup({
  tabs = [],
  activeTabId,
  onTabSelect,
  onTabRename,
  onTabCopy,
  onTabDelete,
  onAddTab,
  canDelete = true,
  canCopy = true,
}) {
  return (
    <TabGroupWrapper role="tablist">
      <TabList>
        {tabs.map(tab => (
          <EditableTab
            key={tab.id}
            tab={tab}
            active={tab.id === activeTabId}
            onSelect={onTabSelect}
            onRename={onTabRename}
            onCopy={onTabCopy}
            onDelete={onTabDelete}
            disableCopy={!canCopy}
            disableDelete={!canDelete}
          />
        ))}
      </TabList>
    </TabGroupWrapper>
  )
}
